import React from 'react';
import PropTypes from 'prop-types';
import GlassIcon from '../ui/GlassIcon';

const HEADING = 'tw-truncate tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 sm:tw-text-2xl lg:tw-text-[1.75rem] dark:tw-text-white';

/**
 * Title card of a data page: icon, title, a one-line description (hidden on
 * phones and short screens) and the page-level actions. `actionsClassName`
 * lays the actions out (default: one wrapping row). A record's own page can
 * add `eyebrow` (above the title, e.g. a back link), `badges` (beside the
 * title) and `meta` (under the title, on phones too).
 */
const PageHero = ({ icon: Icon, gradient, title, subtitle, eyebrow, badges, meta, actionsClassName = 'tw-flex tw-flex-wrap tw-items-center tw-gap-2', children }) => (
  <section className="tw-relative tw-isolate tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/70 tw-p-4 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] sm:tw-p-6 [@media(max-height:820px)]:tw-py-4 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]">
    <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-left-20 -tw-top-24 -tw-z-10 tw-h-60 tw-w-60 tw-rounded-full tw-bg-teal-300/40 tw-blur-3xl dark:tw-bg-teal-500/15" />
    <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-right-10 -tw-top-24 -tw-z-10 tw-h-56 tw-w-56 tw-rounded-full tw-bg-sky-300/30 tw-blur-3xl dark:tw-bg-sky-500/10" />

    <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-gap-5 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
      <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 sm:tw-gap-4">
        <GlassIcon gradient={gradient} size="md" className="sm:tw-h-14 sm:tw-w-14 sm:tw-rounded-[18px]">
          <Icon aria-hidden="true" className="tw-h-6 tw-w-6 sm:tw-h-7 sm:tw-w-7" />
        </GlassIcon>
        <div className="tw-min-w-0">
          {eyebrow}
          {badges ? (
            <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2.5 tw-gap-y-1">
              <h1 className={`tw-min-w-0 ${HEADING}`}>{title}</h1>
              {badges}
            </div>
          ) : (
            <h1 className={HEADING}>{title}</h1>
          )}
          {subtitle && (
            <p className="tw-mt-1 tw-hidden tw-text-sm tw-text-slate-500 sm:tw-block [@media(max-height:820px)]:tw-hidden dark:tw-text-slate-400">
              {subtitle}
            </p>
          )}
          {meta}
        </div>
      </div>

      {children && <div className={actionsClassName}>{children}</div>}
    </div>
  </section>
);

PageHero.propTypes = {
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  eyebrow: PropTypes.node,
  badges: PropTypes.node,
  meta: PropTypes.node,
  actionsClassName: PropTypes.string,
  children: PropTypes.node,
};

export default PageHero;
